import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { brandService } from "../api/services/brandService";
import {
  Brand,
  BrandFilters,
  BrandResult,
} from "../schemas/brandSchema";

export const useBrands = (
  filters: BrandFilters = {},
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();

  const {
    data: brands,
    isLoading: isBrandsLoading,
    refetch,
  } = useQuery<BrandResult, Error>({
    queryKey: ["brands", filters],
    queryFn: () => brandService.getAll(filters),
    enabled: options?.enabled !== false,
  });

  const useGetBrandById = (id?: string) =>
    useQuery<Brand, Error>({
      queryKey: ["brand", id],
      queryFn: () => brandService.getById(id!),
      enabled: !!id,
    });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["brands"] });
    queryClient.invalidateQueries({ queryKey: ["brandOptions"] });
  };

  const createBrandMutation = useMutation({
    mutationFn: brandService.create,
    onSuccess: () => {
      invalidate();
      toast.success("Brand created — login details emailed to the brand");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create brand");
    },
  });

  const updateBrandMutation = useMutation({
    mutationFn: brandService.update,
    onSuccess: () => {
      invalidate();
      toast.success("Brand updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update brand");
    },
  });

  const deleteBrandsMutation = useMutation({
    mutationFn: brandService.remove,
    onSuccess: (_data, ids) => {
      invalidate();
      // Creatives keep pointing at a deleted brand, so refresh their list too.
      queryClient.invalidateQueries({ queryKey: ["creatives"] });
      if (Array.isArray(ids) && ids.length > 1) {
        toast.success(`${ids.length} brands deleted successfully`);
        return;
      }
      toast.success("Brand deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete brand");
    },
  });

  const resetBrandPasswordMutation = useMutation({
    mutationFn: brandService.resetPassword,
    onSuccess: () => {
      toast.success("New password emailed to the brand");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to reset password");
    },
  });

  const assignCreativesMutation = useMutation({
    mutationFn: brandService.assignCreatives,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["creatives"] });
      invalidate();
      const count = variables.creativeIds.length;
      toast.success(
        variables.brandId
          ? `${count} creative(s) assigned`
          : `${count} creative(s) unassigned`,
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to assign creatives");
    },
  });

  return {
    brands,
    refetchBrands: refetch,
    useGetBrandById,
    createBrand: createBrandMutation.mutate,
    updateBrand: updateBrandMutation.mutate,
    deleteBrands: deleteBrandsMutation.mutate,
    resetBrandPassword: resetBrandPasswordMutation.mutate,
    assignCreativesToBrand: assignCreativesMutation.mutate,
    isLoading:
      isBrandsLoading ||
      createBrandMutation.isPending ||
      updateBrandMutation.isPending ||
      deleteBrandsMutation.isPending ||
      resetBrandPasswordMutation.isPending ||
      assignCreativesMutation.isPending,
  };
};
